import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ServicoTomadoService } from '../../../../../shared/services/apis/repo-dfse/servico-tomado.service';
import { ServicoTomadoLookupService } from '../../../../../shared/services/apis/repo-dfse/servico-tomado-lookup.service';
import { ServicoTomadoDetalheStruct } from '../../../../../shared/services/structs/repo-dfse/servico-tomado-detalhe.struct';
import { AuthService } from '../../../../../shared/services/apis/usuario/auth.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { CpfCnpjPipe } from '../../../../../shared/pipes/cpf-cnpj.pipe';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { TipoPessoaEnum } from '../../../../../shared/enums/pessoa/tipo-pessoa.enum';

export interface ImpressaoServicoTomadoData {
  idNFSeEntrada: number;
}

@Component({
  selector: 'app-impressao-servico-tomado-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, AbntPipe],
  providers: [AbntPipe],
  templateUrl: './impressao-servico-tomado-dialog.component.html',
  styleUrls: ['./impressao-servico-tomado-dialog.component.css'],
})
export class ImpressaoServicoTomadoDialogComponent implements OnInit {
  public readonly tipoPessoaEnum = TipoPessoaEnum;

  public isLoading = true;
  public detalhe: ServicoTomadoDetalheStruct | null = null;
  public readonly dataGeracao = new Date();

  public nomeEmpresa = '';
  public cnpjEmpresaFormatado = '';

  private naturezaMap = new Map<string, string>();
  private origemMap = new Map<string, string>();
  private statusFiscalMap = new Map<number, string>();

  private readonly cnpjPipe = new CpfCnpjPipe();

  constructor(
    public dialogRef: MatDialogRef<ImpressaoServicoTomadoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpressaoServicoTomadoData,
    private servicoTomadoService: ServicoTomadoService,
    private servicoTomadoLookupService: ServicoTomadoLookupService,
    private authService: AuthService,
    private alertService: AlertService,
    private abntPipe: AbntPipe,
  ) { }

  ngOnInit(): void {
    const userInfos = this.authService.getUserInfos();
    this.nomeEmpresa = userInfos.nomeEmpresa;
    this.cnpjEmpresaFormatado = this.cnpjPipe.transform(userInfos.cnpj);

    this.servicoTomadoLookupService.getNaturezaBaseCalculoCredito().subscribe(res => {
      (res.list || []).forEach(item => this.naturezaMap.set(item.codigo, item.descricao));
    });
    this.servicoTomadoLookupService.getOrigemCredito().subscribe(res => {
      (res.list || []).forEach(item => this.origemMap.set(item.codigo, item.descricao));
    });
    this.servicoTomadoLookupService.getStatusFiscal().subscribe(res => {
      (res.list || []).forEach(item => this.statusFiscalMap.set(item.id, item.descricao));
    });

    this.servicoTomadoService.getById(this.data.idNFSeEntrada).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isError || !res.detalhe) {
          this.alertService.show('Erro', res.errorDescription || 'Serviço tomado não encontrado.', AlertType.error);
          this.dialogRef.close();
          return;
        }
        this.detalhe = res.detalhe;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao carregar o serviço tomado.', AlertType.error);
        this.dialogRef.close();
      }
    });
  }

  public get documentoEmitenteFormatado(): string {
    if (!this.detalhe?.identificacaoFiscal) return '';
    return this.detalhe.tipoPessoaEmitente === this.tipoPessoaEnum.Fisica
      ? this.detalhe.identificacaoFiscal
      : this.cnpjPipe.transform(this.detalhe.identificacaoFiscal);
  }

  public get enderecoFormatado(): string {
    const d = this.detalhe;
    if (!d) return '';
    const linha1 = [d.logradouro, d.numeroEndereco].filter(Boolean).join(', ');
    const complemento = d.complemento ? ` - ${d.complemento}` : '';
    const municipioUf = d.municipioNome && d.uf ? `${d.municipioNome}/${d.uf}` : (d.municipioNome || d.uf || '');
    return [`${linha1}${complemento}`, d.bairro, municipioUf, d.cep].filter(Boolean).join(' — ');
  }

  public get statusLabel(): string {
    const id = this.detalhe?.idStatusFiscalNFSeEntrada;
    return (id != null && this.statusFiscalMap.get(id)) || '—';
  }

  public get naturezaLabel(): string {
    const codigo = this.detalhe?.codigoNaturezaBaseCalculoCreditoSped;
    if (!codigo) return '';
    return `${codigo} - ${this.naturezaMap.get(codigo) || ''}`;
  }

  public get origemLabel(): string {
    const codigo = this.detalhe?.codigoOrigemCreditoSped;
    if (!codigo) return '';
    return this.origemMap.get(codigo) || codigo;
  }

  public get temImpostosRetidos(): boolean {
    const d = this.detalhe;
    if (!d) return false;
    return !!(d.irValor || d.inssValor || d.issValor || d.pisRetidoValor || d.cofinsRetidoValor || d.csllRetidoValor);
  }

  public get temImpostosCreditados(): boolean {
    const d = this.detalhe;
    if (!d) return false;
    return !!(d.pisCreditado?.valor || d.cofinsCreditado?.valor);
  }

  public formatarValor(valor?: number | null): string {
    return this.abntPipe.transform(valor ?? 0, 'preco', 2);
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  public imprimir(): void {
    const conteudo = document.querySelector('.comprovante-print-container');
    if (!conteudo) return;

    const janelaImpressao = window.open('', '', 'height=900,width=800');
    if (!janelaImpressao) return;

    const styles = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules).map(r => r.cssText).join('');
        } catch { return ''; }
      })
      .join('');

    janelaImpressao.document.write(`
      <html>
        <head>
          <title>Comprovante — Serviço Tomado Nº ${this.detalhe?.numeroNFSe || ''}</title>
          <base href="/">
          <style>
            ${styles}
            *, *::before, *::after { box-sizing: border-box; }
            html, body {
              height: auto !important;
              min-height: 0 !important;
              margin: 0;
              padding: 0;
              font-family: Roboto, "Helvetica Neue", sans-serif;
              color: #000;
              background: #fff;
            }
            .comprovante-print-container {
              display: block !important;
              border: none !important;
              margin: 24px auto !important;
              max-width: 720px !important;
              box-shadow: none !important;
              background: #fff !important;
              color: #000 !important;
            }
            .no-print { display: none !important; }
          </style>
        </head>
        <body>
          ${conteudo.innerHTML}
        </body>
      </html>
    `);

    janelaImpressao.document.close();
    janelaImpressao.focus();
    setTimeout(() => {
      janelaImpressao.print();
      janelaImpressao.close();
    }, 600);
  }
}
